import { Group, Modal, Stack } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { Button, Icon, Text } from '@repo/foundations';
import { useEffect, useState } from 'react';
import type { Monitor } from '../../api';
import { useBulkUpdateMonitors } from '../../api';
import TagPropertySelector from '../../components/Properties/TagPropertySelector';

interface BulkAddTagsModalProps {
	opened: boolean;
	onClose: () => void;
	monitors: Monitor[];
}

export default function BulkAddTagsModal({
	opened,
	onClose,
	monitors,
}: BulkAddTagsModalProps) {
	const { mutateAsync: bulkUpdateMonitors } = useBulkUpdateMonitors({});
	const [selectedTags, setSelectedTags] = useState<string[]>([]);
	const [submitting, setSubmitting] = useState(false);

	useEffect(() => {
		if (opened) {
			setSelectedTags([]);
		}
	}, [opened]);

	const handleTagsChange = (tags: string[]) => {
		setSelectedTags(tags);
	};

	const handleSubmit = async () => {
		if (selectedTags.length === 0) {
			return;
		}

		setSubmitting(true);
		try {
			// Build updates object for bulk update
			const updates: Record<string, Record<string, any>> = {};
			monitors.forEach((monitor) => {
				// Merge existing tags with new tags, removing duplicates
				const updatedTags = Array.from(
					new Set([...(monitor.tags || []), ...selectedTags])
				);
				updates[monitor.id] = {
					tags: updatedTags,
				};
			});

			await bulkUpdateMonitors({
				body: {
					updates,
				},
			});

			showNotification({
				title: 'Tags added',
				message: `Tags added to ${monitors.length} monitor${monitors.length > 1 ? 's' : ''}.`,
				color: 'green',
				icon: <Icon name="check" color="text/success-on-fill/default" />,
			});
			onClose();
		} catch (e) {
			showNotification({
				title: 'Error',
				message: 'Failed to add tags to selected monitors.',
				color: 'red',
				icon: <Icon name="alertCircle" color="text/critical-on-fill/default" />,
			});
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Modal
			opened={opened}
			onClose={onClose}
			title="Add Tags"
			closeOnEscape
			closeOnClickOutside
			size="md"
			maw={500}
		>
			<Stack spacing="lg">
				<Text size="sm" color="text/secondary/default">
					Select tags to add to {monitors.length} monitor
					{monitors.length > 1 ? 's' : ''}. Tags will be added to existing tags
					on each monitor.
				</Text>

				<TagPropertySelector
					initialValue={selectedTags}
					readOnly={submitting}
					placeholder="Select tags to add"
					placeholderIconName="tag"
					forceVariant="default"
					onChange={handleTagsChange}
				/>

				<Group position="right" mt="md" spacing={8}>
					<Button variant="default" onClick={onClose} disabled={submitting}>
						Cancel
					</Button>
					<Button
						variant="primary"
						onClick={handleSubmit}
						loading={submitting}
						disabled={selectedTags.length === 0}
					>
						{`Add Tags to ${monitors.length} Monitor${monitors.length === 1 ? '' : 's'}`}
					</Button>
				</Group>
			</Stack>
		</Modal>
	);
}
